import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { MeDto } from '@zoxo/shared';

import { api, UnauthorizedError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { signOut } from '../../lib/supabase';

/**
 * Current user profile (E2.7, AC-1.3). Plain query — not Suspense — because this
 * is an auth-state check that branches on login: enabled only when a session
 * exists, so signed-out renders don't fire a guaranteed-401 request.
 *
 * GET /users/me lazily provisions the user + auto-generates a nickname on first
 * call. Response is PII-free (MeDto: id/nickname/role).
 */
export function useMe() {
  const { isAuthenticated } = useAuth();

  const query = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeDto>('/users/me'),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
    // A 401 here means the (possibly auto-refreshed) token is no longer valid;
    // retrying just hits another guaranteed 401. The global client already
    // skips retries for 4xx — this is a local guardrail for the same intent.
    retry: (failureCount, error) =>
      error instanceof UnauthorizedError ? false : failureCount < 1,
  });

  // Expired/invalid session (401) → clear it so isAuthenticated flips false and
  // the header shows "카카오로 시작하기" instead of a member label (AC-1.2).
  // signOut delegates to the SDK; onAuthStateChange propagates the new state.
  useEffect(() => {
    if (query.error instanceof UnauthorizedError) {
      void signOut();
    }
  }, [query.error]);

  return query;
}
